import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import departmentService from '../../services/departmentService';

const DepartmentStats = () => {
  const [stats, setStats] = useState({
    totalDepartments: 0,
    activeDepartments: 0,
    totalEmployees: 0,
    averageEmployeesPerDept: 0,
    totalBudget: 0,
    departmentsWithManagers: 0,
    departmentsWithoutManagers: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [departmentData, setDepartmentData] = useState([]);

  useEffect(() => {
    loadDepartmentStats();
  }, [selectedTimeframe]);

  const loadDepartmentStats = async () => {
    try {
      setLoading(true);
      
      // Load all departments for analysis
      const response = await departmentService.getDepartments({ limit: 1000 });
      if (response.success) {
        const departments = response.data.departments || [];
        calculateStats(departments);
        setDepartmentData(departments);
      }
    } catch (error) {
      console.error('Error loading department stats:', error);
      toast.error('Failed to load department statistics');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (departments) => {
    const totalDepartments = departments.length;
    const activeDepartments = departments.filter(dept => dept.isActive).length;
    const totalEmployees = departments.reduce((sum, dept) => sum + (dept.Employees?.length || 0), 0);
    const averageEmployeesPerDept = totalDepartments > 0 ? (totalEmployees / totalDepartments).toFixed(1) : 0;
    const totalBudget = departments.reduce((sum, dept) => sum + (parseFloat(dept.budget) || 0), 0);
    const departmentsWithManagers = departments.filter(dept => dept.Manager).length;
    const departmentsWithoutManagers = totalDepartments - departmentsWithManagers;

    setStats({
      totalDepartments,
      activeDepartments,
      totalEmployees,
      averageEmployeesPerDept,
      totalBudget,
      departmentsWithManagers,
      departmentsWithoutManagers
    });
  };

  const getTopDepartmentsByEmployees = () => {
    return departmentData
      .sort((a, b) => (b.Employees?.length || 0) - (a.Employees?.length || 0))
      .slice(0, 5);
  };

  const getTopDepartmentsByBudget = () => {
    return departmentData
      .filter(dept => dept.budget)
      .sort((a, b) => parseFloat(b.budget) - parseFloat(a.budget))
      .slice(0, 5);
  };

  const getDepartmentsWithoutManagers = () => {
    return departmentData.filter(dept => !dept.Manager);
  };

  const getInactiveDepartments = () => {
    return departmentData.filter(dept => !dept.isActive);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading department statistics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Department Statistics</h2>
            <p className="mt-1 text-gray-600">
              Overview of organizational structure and department metrics
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Time</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <button
              onClick={loadDepartmentStats}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Departments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalDepartments}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEmployees}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg. Employees/Dept</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageEmployeesPerDept}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Budget</p>
              <p className="text-2xl font-bold text-gray-900">
                ${stats.totalBudget.toLocaleString()}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Department Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Active Departments</span>
              <span className="text-sm font-medium text-green-600">{stats.activeDepartments}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Inactive Departments</span>
              <span className="text-sm font-medium text-red-600">
                {stats.totalDepartments - stats.activeDepartments}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">With Managers</span>
              <span className="text-sm font-medium text-blue-600">{stats.departmentsWithManagers}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Without Managers</span>
              <span className="text-sm font-medium text-orange-600">{stats.departmentsWithoutManagers}</span>
            </div>
          </div>
        </div>

        {/* Top Departments by Employees */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Departments by Employees</h3>
          <div className="space-y-3">
            {getTopDepartmentsByEmployees().map((dept, index) => (
              <div key={dept.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                  <span className="text-sm text-gray-900">{dept.name}</span>
                </div>
                <span className="text-sm font-medium text-blue-600">
                  {dept.Employees?.length || 0} employees
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Budget Analysis */}
      {getTopDepartmentsByBudget().length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Departments by Budget</h3>
          <div className="space-y-3">
            {getTopDepartmentsByBudget().map((dept, index) => (
              <div key={dept.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                  <span className="text-sm text-gray-900">{dept.name}</span>
                </div>
                <span className="text-sm font-medium text-green-600">
                  ${parseFloat(dept.budget).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Departments Without Managers */}
        {getDepartmentsWithoutManagers().length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <ArrowTrendingDownIcon className="h-5 w-5 text-orange-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Departments Without Managers</h3>
            </div>
            <div className="space-y-2">
              {getDepartmentsWithoutManagers().map(dept => (
                <div key={dept.id} className="flex items-center justify-between p-2 bg-orange-50 rounded">
                  <span className="text-sm text-gray-900">{dept.name}</span>
                  <span className="text-xs text-orange-600">No Manager</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inactive Departments */}
        {getInactiveDepartments().length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <TrendingDownIcon className="h-5 w-5 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Inactive Departments</h3>
            </div>
            <div className="space-y-2">
              {getInactiveDepartments().map(dept => (
                <div key={dept.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                  <span className="text-sm text-gray-900">{dept.name}</span>
                  <span className="text-xs text-red-600">Inactive</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {stats.totalDepartments === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No departments found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create departments to see statistics and analytics.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentStats; 